import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, getApiUrl } from '../config/api';
import authService, { storageManager, tokenManager } from '../services/auth';

// Debug logging
const DEBUG = true;
const log = (...args) => DEBUG && console.log('[AuthContext]', ...args);

const AuthContext = createContext(null);
const TokenPurchaseModalContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useTokenPurchaseModal = () => {
  const context = useContext(TokenPurchaseModalContext);
  if (!context) {
    throw new Error('useTokenPurchaseModal must be used within an AuthProvider');
  }
  return context;
};

export { TokenPurchaseModalContext };

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [school, setSchool] = useState(null);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);
  const navigate = useNavigate();
  
  // Using singleton instances of managers from services/auth/index.js

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('==== [REFRESH-DEBUG] AUTH INITIALIZATION START ====');
        console.log('[REFRESH-DEBUG] Page URL:', window.location.href);
        console.log('[REFRESH-DEBUG] Cookies present:', document.cookie);
        
        log('Starting auth initialization');
        // Try to restore session from storage
        const storedToken = await storageManager.getStoredToken();
        
        console.log('[REFRESH-DEBUG] Retrieved stored token result:', storedToken ? {
          tokenPresent: !!storedToken.token,
          tokenType: storedToken.type,
          expiresAt: storedToken.expiresAt,
          hasSchoolData: !!storedToken.school,
          hasStudentData: !!storedToken.student
        } : 'null');
        
        if (!storedToken) {
          console.log('[REFRESH-DEBUG] No stored token found, session will be lost');
          setLoading(false);
          return;
        }

        log('Restoring session with stored token:', storedToken);

        // First validate token format locally
        const validation = tokenManager.parseToken(storedToken);
        console.log('[REFRESH-DEBUG] Local token validation result:', validation);
        
        if (!validation.isValid) {
          console.log('[REFRESH-DEBUG] Stored token format invalid:', validation.error);
          await storageManager.clearToken();
          setLoading(false);
          return;
        }

        // Then validate with server
        log('Validating token with server');
        let data;
        try {
          // Use authService to validate the token
          const validationResult = await authService.validateToken(storedToken.token);
          log('Server validation result:', validationResult);
          
          if (!validationResult.success) {
            log('Token validation failed:', validationResult.message);
            await storageManager.clearToken();
            setLoading(false);
            return;
          }
          
          data = validationResult;
        } catch (validationError) {
          log('Server validation error:', validationError);
          await storageManager.clearToken();
          setLoading(false);
          return;
        }

        // Update stored token data with fresh data from server
        let updatedTokenData;
        
        // Handle different token types
        if (storedToken.type === 'school') {
          log('Handling school token type');
          updatedTokenData = {
            token: storedToken.token,
            type: 'school',
            school: storedToken.school,
            expiresAt: data.expiresAt,
            rememberMe: storedToken.rememberMe
          };
          
          // Update auth state for school
          setIsAuthenticated(true);
          setSchool(storedToken.school);
          setStudent(null);
          
          log('Auth state updated for school:', {
            isAuthenticated: true,
            school: storedToken.school
          });
        } else {
          // Default to student type
          log('Handling student token type');
          updatedTokenData = {
            token: storedToken.token,
            type: 'student',
            student: data.student,
            expiresAt: data.expiresAt,
            rememberMe: storedToken.rememberMe
          };
          
          // Update auth state for student
          setIsAuthenticated(true);
          setStudent(data.student);
          setSchool(null);
          
          log('Auth state updated for student:', {
            isAuthenticated: true,
            student: data.student
          });
        }

        log('Storing updated token data:', updatedTokenData);
        await storageManager.storeToken(updatedTokenData);

      } catch (error) {
        log('Auth initialization error:', error);
        await storageManager.clearToken();
        setIsAuthenticated(false);
        setStudent(null);
        setSchool(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Handle cross-tab synchronization
  useEffect(() => {
    const handleAuthChange = async (event) => {
      log('Auth state change event received:', event.detail);
      
      if (event.detail.type === 'TOKEN_UPDATED') {
        try {
          const stored = await storageManager.getStoredToken();
          log('Retrieved stored token after update:', stored ? 'exists' : 'not found');
          
          if (stored) {
            log('Updating auth state with stored data');
            setIsAuthenticated(true);
            
            // Check token type to update correct user information
            if (stored.type === 'school') {
              console.log('[FIX] Setting school data in auth state change handler:', stored.school);
              setSchool(stored.school);
              setStudent(null);
            } else {
              // Default to student type
              console.log('[FIX] Setting student data in auth state change handler:', stored.student);
              setStudent(stored.student);
              setSchool(null);
            }
            
            log('Auth state updated successfully');
          } else {
            log('No token found after TOKEN_UPDATED event');
            setIsAuthenticated(false);
            setStudent(null);
            setSchool(null);
          }
        } catch (error) {
          log('Error handling TOKEN_UPDATED event:', error);
          setIsAuthenticated(false);
          setStudent(null);
          setSchool(null);
        }
      } else if (event.detail.type === 'TOKEN_CLEARED') {
        log('Handling TOKEN_CLEARED event');
        setIsAuthenticated(false);
        setSchool(null);
        setStudent(null);
        // Only reload if this was triggered by a logout action
        if (event.detail.source === 'logout') {
          window.location.reload();
        }
      }
    };

    log('Setting up auth state change listener');
    window.addEventListener('authStateChanged', handleAuthChange);
    
    return () => {
      log('Removing auth state change listener');
      window.removeEventListener('authStateChanged', handleAuthChange);
    };
  }, []);

  // Login function that supports both school login and token-based student authentication
  const login = async (loginData) => {
    log('Starting login process with data:', loginData);
    
    try {
      // Determine login type based on the data provided
      if (loginData.school) {
        // School login
        log('Processing school login');
        
        // Store school data
        const schoolData = {
          ...loginData.school,
          rememberMe: loginData.rememberMe
        };

        log('Storing school data:', schoolData);
        await storageManager.storeToken({
          token: loginData.token,
          type: 'school',
          school: loginData.school,
          rememberMe: loginData.rememberMe
        });

        // Update auth state
        log('Updating auth state for school');
        setIsAuthenticated(true);
        setSchool(loginData.school);
        setStudent(null);
      } else if (loginData.token) {
        // Token-based student authentication
        log('Processing token-based student authentication');
        
        // First validate token format locally
        const tokenData = {
          token: loginData.token,
          type: 'student'
        };
        
        const validation = tokenManager.parseToken(tokenData);
        if (!validation.isValid) {
          log('Local token validation failed:', validation.error);
          throw new Error(validation.error || 'Token inválido');
        }

        log('Local token validation passed');

        // Then validate with server
        log('Validating token with server');
        
        let data;
        try {
          // Use authService to validate the token
          const validationResult = await authService.validateToken(loginData.token);
          
          if (!validationResult.success) {
            throw new Error(validationResult.message || 'Token inválido');
          }
          
          data = validationResult;
        } catch (validationError) {
          log('Server validation failed:', validationError);
          throw new Error(validationError.message || 'Error al validar el token');
        }

        // Store validated token data
        const validatedTokenData = {
          token: loginData.token,
          type: 'student',
          student: data.student,
          expiresAt: data.expiresAt,
          // Default to true for rememberMe with student tokens to ensure long-term persistence
          // This can be overridden if loginData.rememberMe is explicitly provided
          rememberMe: loginData.rememberMe !== undefined ? loginData.rememberMe : true
        };

        log('Storing validated token data:', validatedTokenData);
        await storageManager.storeToken(validatedTokenData);

        // Update auth state
        log('Updating auth state for student');
        setIsAuthenticated(true);
        setStudent(data.student);
        setSchool(null);
      } else {
        throw new Error('Invalid login data provided');
      }

      log('Login process completed successfully');

    } catch (error) {
      log('Login process failed:', error);
      // Clear any partially stored data
      await storageManager.clearToken();
      setIsAuthenticated(false);
      setStudent(null);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const storedToken = await storageManager.getStoredToken();
      
      if (storedToken) {
        try {
          // Invalidate token on server
          const response = await fetch(getApiUrl(API_ENDPOINTS.logout), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token: storedToken.token })
          });

          if (!response.ok) {
            log('Error invalidating token on server:', response.status);
          }
        } catch (error) {
          log('Server logout failed:', error);
          // Continue with local logout even if server fails
        }
      }
    } catch (error) {
      log('Local logout error:', error);
    } finally {
      // Always clear local state with source information
      await storageManager.clearToken('logout');
      setIsAuthenticated(false);
      setStudent(null);
      setSchool(null);
    }
  };

  // Get current token
  const getToken = async () => {
    try {
      const storedToken = await storageManager.getStoredToken();
      return storedToken?.token || null;
    } catch (error) {
      log('Get token error:', error);
      return null;
    }
  };

  const showPurchaseModal = () => setShowTokenPurchaseModal(true);
  const hidePurchaseModal = () => setShowTokenPurchaseModal(false);

  const value = {
    isAuthenticated,
    school,
    student,
    loading,
    login,
    logout,
    getToken,
    showTokenPurchaseModal,
    showPurchaseModal,
    hidePurchaseModal
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      <TokenPurchaseModalContext.Provider value={{
        isOpen: showTokenPurchaseModal,
        onClose: hidePurchaseModal
      }}>
        {children}
      </TokenPurchaseModalContext.Provider>
    </AuthContext.Provider>
  );
};

export default AuthContext;